import { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Phone, MapPin } from "lucide-react";
import { LGA_COORDS, normLga, titleLga } from "@/lib/lgaCoords";

const ShopRow = ({ v }) => (
  <div className="py-2 border-b border-gray-100 last:border-0" data-testid={`map-shop-${v.vendor_id}`}>
    <div className="font-semibold text-emerald-950 text-sm">{v.business_name}</div>
    <div className="text-xs text-gray-600 flex gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0 text-emerald-600" />{v.address || "—"}</div>
    {v.landmark && <div className="text-xs text-gray-500 ml-4">Near: {v.landmark}</div>}
    {v.phone && (
      <a href={`tel:${v.phone}`} data-testid={`map-call-${v.vendor_id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold !text-white hover:bg-emerald-700">
        <Phone className="w-3 h-3" /> Call shop
      </a>
    )}
  </div>
);

export function groupByLga(vendors, stateCoords) {
  const groups = {};
  vendors.forEach((v) => {
    const st = (v.state || "").trim();
    const lga = normLga(v.lga);
    const key = `${st.toLowerCase()}|${lga}`;
    const pos = LGA_COORDS[key] || stateCoords[st];
    if (!pos) return;
    if (!groups[key]) groups[key] = { key, state: st, lga: titleLga(lga || "unknown"), pos, approx: !LGA_COORDS[key], shops: [] };
    groups[key].shops.push(v);
  });
  return Object.values(groups).sort((a, b) => b.shops.length - a.shops.length);
}

export const VendorLgaMap = ({ vendors, stateCoords, height = 480 }) => {
  const groups = useMemo(() => groupByLga(vendors, stateCoords), [vendors, stateCoords]);
  const center = groups[0]?.pos || [9.08, 8.68];
  const bounds = groups.length > 1 ? groups.map((g) => g.pos) : undefined;
  return (
    <div className="rounded-2xl overflow-hidden border border-emerald-100 relative z-0" data-testid="vendor-lga-map">
      <MapContainer key={groups.map((g) => g.key).join(",")} center={bounds ? undefined : center} zoom={bounds ? undefined : 10} bounds={bounds} boundsOptions={{ padding: [40, 40], maxZoom: 10 }} scrollWheelZoom={false} style={{ height }}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {groups.map((g) => (
          <CircleMarker key={g.key} center={g.pos} radius={Math.min(10 + g.shops.length * 0.6, 26)}
            pathOptions={{ color: "#065f46", fillColor: "#10b981", fillOpacity: 0.75, weight: 2 }}>
            <Tooltip direction="top" offset={[0, -8]}>{g.lga}: {g.shops.length} shop{g.shops.length > 1 ? "s" : ""}</Tooltip>
            <Popup maxWidth={300}>
              <div data-testid={`lga-popup-${g.key}`}>
                <div className="font-display font-bold text-emerald-900">{g.lga}, {g.state}</div>
                <div className="text-xs text-gray-500 mb-1">{g.shops.length} approved TMF shop{g.shops.length > 1 ? "s" : ""}{g.approx ? " (marker shows the state)" : ""}</div>
                <div className="max-h-64 overflow-auto pr-1">{g.shops.map((v) => <ShopRow key={v.vendor_id} v={v} />)}</div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
      <div className="absolute bottom-2 left-2 z-[400] rounded-md bg-white/90 px-2 py-1 text-[11px] text-gray-600">Each circle is one LGA. Tap it to see its shops.</div>
    </div>
  );
};
